import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as dailyReportActions from '@/components/dailyReport/Actions';
import * as errorHandlerActions from '@/components/errorHandler/Actions';
import * as learningPathActions from '@/pages/learningPath/Actions';
import * as sharedActions from '@/shared/Actions';
import {
	FRONTEND_ROUTES,
	getExperimentPath,
	getUpdateUserPath,
	IS_ADMIN,
	IS_LOCAL_ENVIRONMENT,
	LOGOUT_REDIRECT_URL,
	MARKETING_URL,
	ROUTES_API,
} from '@/services/Constants';
import { sharedTypes } from '@/shared/Constants';
import { identifyHotjar } from '@/services/Helper';
import { deleteExperiment, handleExperiment, isLearningPath } from '@/shared/Helpers';
import { getUserId } from '@/features/onboarding';
import { removeAccessToken } from '@/services/AccessToken';
import { processRequest } from '@/services/Api';
import { historyReplace } from '@/services/History';

export default function* () {
	yield all([
		yield takeLatest(sharedTypes.GET_USER_DATA_REQUEST, handleGetUserDataRequest),
		yield takeLatest(sharedTypes.LOGOUT, handleUserLogout),
		yield takeLatest(sharedTypes.UPDATE_USER_DATA_REQUEST, handleUpdateUserDataRequest),
		yield takeLatest(sharedTypes.SEND_EVENT_REQUEST, handleSendEventRequest),
		yield takeLatest(sharedTypes.HANDLE_USER_EXPERIMENT, handleUserExperimentRequest),
	]);
}

export function* handleGetUserDataRequest({ payload }) {
	try {
		const { options } = payload || {};

		const { data: userData } = yield call(processRequest, ROUTES_API.GET_USER_DATA);

		const {
			needs_to_fill_grade,
			child_name,
			grade_id,
			sidk_user,
			sidk_closed,
			show_autonomy_experiment,
			play_mode,
		} = userData || {};

		if (sidk_user && sidk_closed) {
			yield put(sharedActions.logout());
			window.location.href = `${MARKETING_URL}school-gesloten`;
			return;
		}

		yield put(sharedActions.getUserDataSuccess(userData));

		if (IS_ADMIN) return;

		identifyHotjar(userData);

		if (options?.skipOnboardingCheck) return;

		if (!grade_id) {
			yield call(historyReplace, FRONTEND_ROUTES.ONBOARDING_CHOOSE_GRADE);
			return;
		}

		if (needs_to_fill_grade) {
			yield call(historyReplace, FRONTEND_ROUTES.NEW_SCHOOL_YEAR);
		}

		if (!child_name) {
			yield call(historyReplace, FRONTEND_ROUTES.SAVE_CHILD_NAME);
			return;
		}

		if (show_autonomy_experiment) {
			yield put(learningPathActions.setAutonomyMode(true));
		}

		const { data: dailyReport } = yield call(processRequest, ROUTES_API.DAILY_REPORT);
		yield put(dailyReportActions.getDailyReportSuccess(dailyReport));

		identifyHotjar(userData, dailyReport);

		if (isLearningPath(play_mode)) {
			yield put(learningPathActions.getLearningPathRequest());
		}
	} catch (e) {
		yield put(sharedActions.getUserDataError());
		yield put(errorHandlerActions.handleError(e, 'page-blocking'));
	}
}

export function* handleUserLogout() {
	try {
		if (IS_ADMIN || IS_LOCAL_ENVIRONMENT) {
			yield call(processRequest, ROUTES_API.INVALIDATE_TOKEN, 'POST');
			yield call(removeAccessToken);
			window.location.href = FRONTEND_ROUTES.LOGIN;
		} else {
			window.location.href = LOGOUT_REDIRECT_URL;
		}
	} catch (e) {
		yield put(sharedActions.getUserDataError());
		yield put(errorHandlerActions.handleError(e, 'ignore'));
	}
}

export function* handleUpdateUserDataRequest({ payload }) {
	try {
		const userId = yield select(getUserId);
		const { data } = payload;

		yield call(processRequest, getUpdateUserPath(userId), 'PUT', {
			user: data,
		});

		const { data: userData } = yield call(processRequest, ROUTES_API.GET_USER_DATA);

		yield put(sharedActions.getUserDataSuccess(userData));

		yield put(sharedActions.updateUserDataSuccess());
	} catch (e) {
		yield put(sharedActions.updateUserDataError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleSendEventRequest({ payload }) {
	try {
		const { data: requestData } = payload;
		if (IS_LOCAL_ENVIRONMENT) {
			console.log('send_event', requestData);
		} else {
			yield call(processRequest, ROUTES_API.SEND_EVENT, 'POST', requestData);
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(sharedActions.sendEventError());
	}
}

export function* handleUserExperimentRequest({ payload }) {
	const { experimentName } = payload;

	try {
		const { data } = yield call(processRequest, getExperimentPath(experimentName));
		if (data.experiment) {
			yield call(handleExperiment, data.experiment);
		}
		yield put(sharedActions.handleUserExperimentSuccess(experimentName));
	} catch (e) {
		yield call(deleteExperiment, experimentName);
		yield put(sharedActions.handleUserExperimentSuccess(experimentName));
		yield put(errorHandlerActions.handleError(e, 'ignore'));
	}
}
